import React, { useCallback } from 'react';

import { Subscriber } from '@atlaskit/rovo-triggers/main';
import { type Payload, Topics } from '@atlaskit/rovo-triggers/types';

import { useActiveAction } from '@atlassian/conversation-assistant/controllers/active-action';
import type { RovoChatRemixSession } from '@atlassian/conversation-assistant/ui/rovo-chat-remix-view';
import {
	type BaseConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';

import { manuallyLogExpExposure } from '@confluence/feature-experiments';
import { LoadableLazy } from '@confluence/loadable';

import { renderRovoChatRemixPicker } from './renderRovoChatRemixPicker.tsx';

const LazyRovoChatRemixView = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ConfluenceRovoChatRemixView" */ '@atlassian/conversation-assistant/ui/rovo-chat-remix-view'
			)
		).RovoChatRemixView,
});

type RovoRemixConversationAction = BaseConversationAction & {
	presentation: {
		hideActionHeader: true;
		suppressPromptLibrary: true;
		fillAvailableHeight: true;
	};
};

const createRovoRemixAction = (session: RovoChatRemixSession): RovoRemixConversationAction => ({
	id: `confluence-rovo-remix:${crypto.randomUUID()}`,
	title: 'Remix',
	handleType: HandleActionType.Base,
	icon: () => <React.Fragment />,
	presentation: {
		hideActionHeader: true,
		suppressPromptLibrary: true,
		fillAvailableHeight: true,
	},
	viewConfig: {
		render: () => (
			<LazyRovoChatRemixView
				renderRovoChatRemixPicker={renderRovoChatRemixPicker}
				session={session}
			/>
		),
	},
});

export const RovoRemixActionLauncher = (): React.JSX.Element => {
	const { setActiveAction } = useActiveAction();
	const handleEvent = useCallback(
		(payload: Payload) => {
			if (payload.type !== 'open-rovo-remix-sidebar') {
				return;
			}

			// Single exposure point for all variant2 Remix entry paths.
			manuallyLogExpExposure('cc-ce-maui-remix-menu-multivariant');
			setActiveAction(
				createRovoRemixAction({
					consumer: payload.data.remixConsumer,
					scope: payload.data.remixPreset,
				}),
			);
		},
		[setActiveAction],
	);

	return (
		<Subscriber
			consumeOnceKey="confluence-rovo-remix-action-launcher"
			onEvent={handleEvent}
			topic={Topics.AI_MATE}
			triggerLatest
		/>
	);
};
